<template>
  <KeyValuePair
    :label="label"
    :value="rangeStringForTooltip"
    class="location-card-date-range"
  >
    <template v-if="slots.label" #label>
      <slot name="label" />
    </template>
    <template #value>
      <div>{{ dateRangeString }}</div>
      <div>{{ timeRangeString }}</div>
    </template>
  </KeyValuePair>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';

import { renderDateTime } from '@rygen/rygen-ui/i18n';

import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const props = defineProps({
  label: {
    type: String,
    default: () => '',
  },
  start: {
    type: Object as PropType<DateTime>,
    default: () => undefined,
  },
  end: {
    type: Object as PropType<DateTime>,
    default: () => undefined,
  },
});
const { label, start, end } = toRefs(props);

const slots = defineSlots<{
  label?: () => any;
}>();
const startDateString = computed(() => {
  return renderDateTime(start?.value, {
    timeFormat: 'NONE',
    defaultValue: '',
  });
});

const startTimeString = computed(() => {
  return renderDateTime(start?.value, {
    dateFormat: 'NONE',
    defaultValue: '',
    timeFormat: {
      hour: 'numeric',
      minute: 'numeric',
      // Only show the timezone after `start` if there's no `end`, otherwise
      // we'll show the timezone after `end`
      timeZoneName: !end?.value?.zoneName ? 'short' : undefined,
    },
    timeZone: start?.value?.zoneName ?? undefined,
  });
});

const endDateString = computed(() => {
  return renderDateTime(end?.value, {
    timeFormat: 'NONE',
    defaultValue: '',
  });
});

const endTimeString = computed(() => {
  return renderDateTime(end?.value, {
    dateFormat: 'NONE',
    defaultValue: '',
    timeFormat: {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    },
    timeZone: end?.value?.zoneName ?? undefined,
  });
});

const dateRangeString = computed(() => {
  const displayStr = [startDateString.value, endDateString.value]
    .filter((d) => !!d)
    .join(' - ')
    .trim();
  return displayStr ? displayStr : '-';
});

const timeRangeString = computed(() => {
  const displayStr = [startTimeString.value, endTimeString.value]
    .filter((d) => !!d)
    .join(' - ')
    .trim();
  return displayStr ? displayStr : '-';
});

const rangeStringForTooltip = computed(() => {
  const displayStr = [dateRangeString.value, timeRangeString.value]
    .filter((d) => !!d)
    .join('\n')
    .trim();
  return displayStr ? displayStr : '-';
});
</script>
